import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LectureTreeNode } from '../../types/Lecture';
import { buildChildrenMap } from '../../utils/lectureTree';
import { logout } from '../auth/authSlice';

export type LectureTreeStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface LectureTreeState {
  examId: string | null;
  nodesById: Record<string, LectureTreeNode>;
  orderedIds: string[];
  status: LectureTreeStatus;
  expandedIds: string[];
  /**
   * Exam the current `expandedIds` describe. A tree for a different exam (or
   * the very first tree of a session) has no expansion state yet: it opens
   * every folder except the ones the member collapsed on an earlier visit
   * (server-kept, ORA-567), so a fresh load never hides documents the user
   * didn't choose to hide.
   */
  expansionExamId: string | null;
  showHidden: boolean;
  /**
   * Highest tree request issued so far. Responses are applied in request order
   * so a slow fetch for a previous exam can never land after a newer one (which
   * would leave this slice describing a different exam than the lectures slice).
   */
  latestRequestId: number | null;
  /**
   * Responses issued at or before this id describe the tree as it was BEFORE a
   * local mutation (create/delete/move) was applied, so they are dropped —
   * otherwise a refresh that was already in flight reverts the mutation, or
   * resurrects a just-deleted node. Cleared once a newer response lands.
   */
  responseCutoffRequestId: number | null;
}

const initialState: LectureTreeState = {
  examId: null,
  nodesById: {},
  orderedIds: [],
  status: 'idle',
  expandedIds: [],
  expansionExamId: null,
  showHidden: false,
  latestRequestId: null,
  responseCutoffRequestId: null,
};

const compareDecimalStrings = (a: string, b: string): number => {
  if (a === b) return 0;
  const aNegative = a.startsWith('-');
  const bNegative = b.startsWith('-');
  if (aNegative !== bNegative) return aNegative ? -1 : 1;

  const [aIntegerRaw, aFraction = ''] = a.replace(/^[+-]/, '').split('.');
  const [bIntegerRaw, bFraction = ''] = b.replace(/^[+-]/, '').split('.');
  const aInteger = aIntegerRaw.replace(/^0+(?=\d)/, '');
  const bInteger = bIntegerRaw.replace(/^0+(?=\d)/, '');

  if (aInteger.length !== bInteger.length) {
    const comparison = aInteger.length - bInteger.length;
    return aNegative ? -comparison : comparison;
  }

  const integerComparison = aInteger.localeCompare(bInteger);
  if (integerComparison !== 0) {
    return aNegative ? -integerComparison : integerComparison;
  }

  const fractionLength = Math.max(aFraction.length, bFraction.length);

  const fractionComparison = aFraction
    .padEnd(fractionLength, '0')
    .localeCompare(bFraction.padEnd(fractionLength, '0'));

  return aNegative ? -fractionComparison : fractionComparison;
};

const sortTreeIds = (
  nodesById: Record<string, LectureTreeNode>,
  previousOrder: readonly string[]
): string[] => {
  const previousIndex = new Map(
    previousOrder.map((id, index) => [id, index] as const)
  );

  const children = buildChildrenMap(nodesById);

  const sortSiblings = (nodes: LectureTreeNode[]) =>
    nodes.sort((a, b) => {
      const orderComparison = compareDecimalStrings(a.orderKey, b.orderKey);
      if (orderComparison !== 0) return orderComparison;
      return (
        (previousIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (previousIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER)
      );
    });

  children.forEach(sortSiblings);

  const orderedIds: string[] = [];
  const visited = new Set<string>();

  const visit = (node: LectureTreeNode) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);
    orderedIds.push(node.id);
    (children.get(node.id) ?? []).forEach(visit);
  };

  (children.get(null) ?? []).forEach(visit);

  // Keep malformed/orphaned records available until the next authoritative
  // fetch instead of silently dropping them from the rendered ordering.
  Object.values(nodesById)
    .filter((node) => !visited.has(node.id))
    .sort(
      (a, b) =>
        (previousIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (previousIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    )
    .forEach(visit);

  return orderedIds;
};

/**
 * A local mutation makes every response issued so far obsolete: it was built
 * from the pre-mutation tree, so applying it would silently undo the change.
 */
const invalidateInFlightResponses = (state: LectureTreeState) => {
  state.responseCutoffRequestId = state.latestRequestId;
};

type MovedNode = {
  id: string;
  parentId: string | null;
  orderKey: string;
};

const applyNodeMove = (state: LectureTreeState, moved: MovedNode): boolean => {
  const movedNode = state.nodesById[moved.id];
  if (!movedNode) return false;

  const oldDepth = movedNode.depth;

  const newDepth = moved.parentId
    ? (state.nodesById[moved.parentId]?.depth ?? 0) + 1
    : 1;

  const depthDelta = newDepth - oldDepth;
  const children = buildChildrenMap(state.nodesById);

  const updateDescendantDepth = (parentId: string) => {
    (children.get(parentId) ?? []).forEach((child) => {
      child.depth += depthDelta;
      updateDescendantDepth(child.id);
    });
  };

  movedNode.parentId = moved.parentId;
  movedNode.orderKey = moved.orderKey;
  movedNode.depth = newDepth;
  if (depthDelta !== 0) updateDescendantDepth(movedNode.id);

  return true;
};

export const lectureTreeSlice = createSlice({
  name: 'lectureTree',
  initialState,
  reducers: {
    treeLoading: (
      state,
      action: PayloadAction<{
        examId: string;
        requestId: number;
        /**
         * Background refresh: claim the request id (so a stale response is
         * still dropped) without blanking the rendered tree into a skeleton.
         */
        background?: boolean;
      }>
    ) => {
      state.latestRequestId = action.payload.requestId;
      if (action.payload.background) return;

      // Switching exams: the rendered nodes belong to the exam we are leaving,
      // and the skeleton only shows while there are none — without this the
      // previous exam's folders (and their checkbox state) stay on screen under
      // the new exam's header for the whole fetch.
      if (state.examId !== action.payload.examId) {
        state.nodesById = {};
        state.orderedIds = [];
      }

      state.examId = action.payload.examId;
      state.status = 'loading';
    },
    /**
     * A claimed request that will never dispatch `treeReceived` (its response
     * was discarded, or the fetch failed). Releasing the id keeps an OLDER
     * request that is still in flight applicable — otherwise its response is
     * dropped as stale and the panel stays in `loading` forever, rendering
     * whichever exam's nodes it last held.
     */
    treeRequestAbandoned: (
      state,
      action: PayloadAction<{
        requestId: number;
        previousRequestId: number | null;
      }>
    ) => {
      if (state.latestRequestId !== action.payload.requestId) return;
      state.latestRequestId = action.payload.previousRequestId;
    },
    treeReceived: (
      state,
      action: PayloadAction<{
        examId: string;
        nodes: LectureTreeNode[];
        requestId?: number;
        /**
         * Folders the member collapsed on an earlier visit (per user and exam,
         * kept on the server). Only consulted for the first tree of an exam;
         * a same-exam refresh keeps the in-session expansion instead.
         */
        collapsedIds?: string[] | null;
      }>
    ) => {
      const { requestId } = action.payload;

      if (
        requestId !== undefined &&
        state.latestRequestId !== null &&
        requestId < state.latestRequestId
      ) {
        return;
      }

      // Issued before a local mutation, so it still describes the pre-mutation
      // tree (see invalidateInFlightResponses).
      if (
        requestId !== undefined &&
        state.responseCutoffRequestId !== null &&
        requestId <= state.responseCutoffRequestId
      ) {
        return;
      }

      if (requestId !== undefined) state.latestRequestId = requestId;
      state.responseCutoffRequestId = null;
      state.examId = action.payload.examId;

      const previousNodesById = state.nodesById;

      state.nodesById = Object.fromEntries(
        action.payload.nodes.map((node) => [node.id, node])
      );

      state.orderedIds = action.payload.nodes.map(({ id }) => id);

      const folderIds = action.payload.nodes
        .filter((node) => node.nodeType === 'FOLDER')
        .map(({ id }) => id);

      // A foreground exam switch blanks the nodes before the new tree lands;
      // if the user switched straight back, `expansionExamId` still names this
      // exam but describes nothing, and the refresh branch below would treat
      // every folder as newly appeared. Rebuild from the stored set instead.
      const isFirstTreeForExam =
        state.expansionExamId !== action.payload.examId ||
        Object.keys(previousNodesById).length === 0;

      if (isFirstTreeForExam) {
        // First tree for this exam: show everything the user has, minus what
        // they collapsed last time. Folders that appeared since default open.
        const collapsed = new Set(action.payload.collapsedIds ?? []);
        state.expandedIds = folderIds.filter((id) => !collapsed.has(id));
      } else {
        // A refresh keeps the in-session expansion, prunes folders that are
        // gone, and defaults folders that just appeared to expanded.
        const keptIds = state.expandedIds.filter(
          (id) => state.nodesById[id]?.nodeType === 'FOLDER'
        );

        const newFolderIds = folderIds.filter((id) => !previousNodesById[id]);

        state.expandedIds = [...new Set([...keptIds, ...newFolderIds])];
      }

      state.expansionExamId = action.payload.examId;

      state.status = 'succeeded';
    },
    treeFailed: (state) => {
      state.status = 'failed';
    },
    nodeUpserted: (state, action: PayloadAction<LectureTreeNode>) => {
      invalidateInFlightResponses(state);

      const isNewFolder =
        !state.nodesById[action.payload.id] &&
        action.payload.nodeType === 'FOLDER';

      state.nodesById[action.payload.id] = {
        ...state.nodesById[action.payload.id],
        ...action.payload,
      };

      if (isNewFolder) state.expandedIds.push(action.payload.id);

      state.orderedIds = sortTreeIds(state.nodesById, state.orderedIds);
    },
    nodesRemoved: (state, action: PayloadAction<string[]>) => {
      invalidateInFlightResponses(state);

      const removedIds = new Set(action.payload);

      action.payload.forEach((id) => {
        delete state.nodesById[id];
      });

      state.orderedIds = state.orderedIds.filter((id) => !removedIds.has(id));
      state.expandedIds = state.expandedIds.filter((id) => !removedIds.has(id));
    },
    nodeMoved: (state, action: PayloadAction<MovedNode>) => {
      if (!applyNodeMove(state, action.payload)) return;

      invalidateInFlightResponses(state);
      state.orderedIds = sortTreeIds(state.nodesById, state.orderedIds);
    },
    nodesMoved: (state, action: PayloadAction<MovedNode[]>) => {
      if (!action.payload.some((moved) => state.nodesById[moved.id])) return;

      invalidateInFlightResponses(state);
      action.payload.forEach((moved) => applyNodeMove(state, moved));
      state.orderedIds = sortTreeIds(state.nodesById, state.orderedIds);
    },
    toggleExpanded: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.nodesById[id]?.nodeType !== 'FOLDER') return;

      state.expandedIds = state.expandedIds.includes(id)
        ? state.expandedIds.filter((expandedId) => expandedId !== id)
        : [...state.expandedIds, id];
    },
    setExpanded: (state, action: PayloadAction<string[]>) => {
      state.expandedIds = [...new Set(action.payload)].filter(
        (id) => state.nodesById[id]?.nodeType === 'FOLDER'
      );
    },
    setShowHidden: (state, action: PayloadAction<boolean>) => {
      state.showHidden = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  treeLoading,
  treeRequestAbandoned,
  treeReceived,
  treeFailed,
  nodeUpserted,
  nodesRemoved,
  nodeMoved,
  nodesMoved,
  toggleExpanded,
  setExpanded,
  setShowHidden,
} = lectureTreeSlice.actions;

export default lectureTreeSlice.reducer;
